/**
 * Layout settings in the right drawer: the chrome's theme, sidebar Pin or Float, Lykke's panel, and the way back to
 * the classic layout. Each choice is kept per person (layoutPref.ts).
 */
import { cn } from '@/components/ui/utils';
import { useShell } from './ShellContext';
import { useNewLayout } from './useNewLayout';
import type { ShellTheme, SidebarMode } from './layoutPref';

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<{ id: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-[var(--shell-text)]">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex gap-0.5 rounded-xl bg-[var(--shell-hover)] p-0.5">
        {options.map((o) => (
          <button key={o.id} type="button" role="radio" aria-checked={value === o.id} onClick={() => onChange(o.id)}
            className={cn('flex-1 rounded-[10px] px-2 py-1.5 text-[12.5px] font-semibold transition-colors',
              value === o.id ? 'bg-[var(--shell-surface)] text-[var(--shell-text)] shadow-[0_1px_2px_rgba(16,24,40,0.08)]' : 'text-[var(--shell-muted)] hover:text-[var(--shell-text)]')}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ShellSettings() {
  const shell = useShell();
  const layout = useNewLayout();
  if (!shell) return null;
  const { prefs, setPrefs } = shell;
  return (
    <div className="flex flex-col gap-5 px-4 pt-1" data-shell-settings>
      <Choice<ShellTheme> label="Theme" value={prefs.theme} onChange={(theme) => setPrefs({ theme })}
        options={[{ id: 'light', label: 'Light' }, { id: 'dark', label: 'Dark' }, { id: 'system', label: 'System' }]} />
      {!shell.isPhone && <Choice<SidebarMode> label="Sidebar" value={prefs.sidebar} onChange={(sidebar) => setPrefs({ sidebar })}
        options={[{ id: 'pinned', label: 'Pinned open' }, { id: 'float', label: 'Float (opens on hover)' }]} />}
      {!shell.isPhone && <Choice<'open' | 'closed'> label="Lykke" value={shell.agentOpen ? 'open' : 'closed'} onChange={(v) => shell.setAgentOpen(v === 'open')}
        options={[{ id: 'open', label: 'Shown' }, { id: 'closed', label: 'Hidden' }]} />}
      <p className="text-[12px] leading-relaxed text-[var(--shell-muted)]">{shell.isPhone ? 'On a phone the sidebar is behind the menu button and Lykke opens from her button at the bottom.' : 'Ctrl J shows or hides Lykke. Drag her edge to make the panel wider.'} In dark mode the pages keep their light look for now.</p>
      {layout.mayToggle && (
        <div className="flex flex-col gap-1.5 border-t border-[var(--shell-border)] pt-4">
          <span className="text-[12.5px] font-semibold text-[var(--shell-text)]">New layout (beta)</span>
          <span className="text-[12px] text-[var(--shell-muted)]">One Lykke on every screen, a retractable sidebar and a workspace. Switch back any time in Settings.</span>
          <button type="button" onClick={() => { shell.closeDrawer(); layout.set(false); }} className="mt-1 self-start rounded-full border border-[var(--shell-border-strong)] px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--shell-text)] hover:bg-[var(--shell-hover)]">
            Back to the classic layout
          </button>
        </div>
      )}
    </div>
  );
}
